import { PrintSize, OutputFormat } from '../types';
import * as pdfjsLib from 'pdfjs-dist';
import ExifReader from 'exifreader';
import { toast } from 'react-hot-toast';

// FileInfo tipini tanımla
interface FileInfo {
  dimensions: { width: number; height: number };
  dpi: number;
  fileType: string;
  size: number;
}

// Doğrudan sabit bir URL kullanarak sorunu çözelim
const API_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' 
  ? 'http://localhost:3000/api'
  : window.location.origin + '/api';  // Dinamik olarak canlı sunucu URL'sini kullan

console.log('imageProcessing.ts - API_URL:', API_URL, 'Host:', window.location.hostname);

// Görsel işleme fonksiyonunu basitleştiriyoruz
export const processImage = async (
  file: File,
  targetSize: PrintSize,
  outputFormat: OutputFormat,
  dpi: number = 300
): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = async () => {
      try {
        // Canvas oluştur
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        
        if (!ctx) {
          throw new Error('Canvas context oluşturulamadı');
        }

        // DPI'a göre boyutları hesapla
        const targetWidth = Math.round(targetSize.width * dpi);
        const targetHeight = Math.round(targetSize.height * dpi);

        // En boy oranını koru
        const sourceAspectRatio = img.width / img.height;
        const targetAspectRatio = targetWidth / targetHeight;

        let sourceX = 0;
        let sourceY = 0;
        let sourceWidth = img.width;
        let sourceHeight = img.height;

        // Kaynak görüntüden kesilecek alanı hesapla
        if (sourceAspectRatio > targetAspectRatio) {
          // Görüntü daha geniş, yanlardan kes
          sourceWidth = Math.round(img.height * targetAspectRatio);
          sourceX = Math.round((img.width - sourceWidth) / 2);
        } else {
          // Görüntü daha uzun, üst ve alttan kes
          sourceHeight = Math.round(img.width / targetAspectRatio);
          sourceY = Math.round((img.height - sourceHeight) / 2);
        }

        // Canvas boyutlarını ayarla
        canvas.width = targetWidth;
        canvas.height = targetHeight;

        // Büyütme/küçültme kalitesini en yükseğe çek (Chrome varsayılanı 'low')
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        // Görüntüyü çiz
        ctx.drawImage(
          img,
          sourceX, sourceY, sourceWidth, sourceHeight, // Kaynak koordinatları
          0, 0, targetWidth, targetHeight // Hedef koordinatları
        );

        // Canvas'ı blob'a dönüştür.
        // Tarayıcılar 'image/pdf' üretemez (sessizce PNG'ye düşer ve .pdf uzantılı bozuk dosya çıkar),
        // bu yüzden PDF için önce yüksek kaliteli JPEG üretip sonra gerçek bir PDF'e gömüyoruz.
        const canvasMime = outputFormat === 'pdf' ? 'image/jpeg' : `image/${outputFormat}`;
        const blob = await new Promise<Blob>((resolve, reject) => {
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('Görüntü işlenemedi'));
                return;
              }
              resolve(blob);
            },
            canvasMime,
            canvasMime === 'image/jpeg' ? 0.95 : undefined
          );
        });

        if (outputFormat === 'pdf') {
          resolve(await jpegToPdf(blob, targetSize.width, targetSize.height));
          return;
        }

        // DPI metadata'sını ekle
        let finalBlob;
        if (outputFormat === 'png') {
          finalBlob = await addMetadataToPng(blob, dpi);
          
          // DPI değerini kontrol et
          const checkDpi = await readPngDpi(new File([finalBlob], 'temp.png', { type: 'image/png' }));
          console.log(`PNG DPI kontrolü: ${checkDpi} DPI (hedef: ${dpi} DPI)`);
          
          if (Math.abs(checkDpi - dpi) > 1) {
            console.warn('DPI değeri doğru ayarlanamadı, tekrar deneniyor...');
            finalBlob = await addDpiMetadataToPng(blob, dpi);
          }
        } else if (outputFormat === 'jpeg') {
          finalBlob = await addMetadataToJpeg(blob, dpi);
        } else {
          finalBlob = blob;
        }

        resolve(finalBlob);
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = () => reject(new Error('Görüntü yüklenemedi'));
    img.src = URL.createObjectURL(file);
  });
};

// JPEG görüntüyü baskı boyutunda (inç -> 72 pt/inç) tek sayfalık gerçek bir PDF'e gömer
async function jpegToPdf(jpegBlob: Blob, widthInches: number, heightInches: number): Promise<Blob> {
  const { PDFDocument } = await import('pdf-lib');
  const pdfDoc = await PDFDocument.create();
  const pageWidth = widthInches * 72;
  const pageHeight = heightInches * 72;
  const page = pdfDoc.addPage([pageWidth, pageHeight]);
  const image = await pdfDoc.embedJpg(new Uint8Array(await jpegBlob.arrayBuffer()));
  page.drawImage(image, { x: 0, y: 0, width: pageWidth, height: pageHeight });
  const bytes = await pdfDoc.save();
  return new Blob([bytes], { type: 'application/pdf' });
}

// Metadata ekleme fonksiyonları
async function addMetadataToJpeg(blob: Blob, dpi: number): Promise<Blob> {
  const buffer = await blob.arrayBuffer();
  const metadata = addJpegMetadata(new Uint8Array(buffer), dpi);
  return new Blob([metadata], { type: 'image/jpeg' });
}

// PNG için metadata ekleme fonksiyonu - daha güvenilir bir yaklaşım
async function addMetadataToPng(blob: Blob, dpi: number): Promise<Blob> {
  console.log(`PNG DPI ayarlama başladı - Hedef DPI: ${dpi}`);
  
  try {
    // Doğrudan client-side işleme yap
    const buffer = await blob.arrayBuffer();
    const fixedPng = await enhancedPngDpiAdjust(new Uint8Array(buffer), dpi);
    
    // DPI değerini kontrol et
    const result = new Blob([fixedPng], { type: 'image/png' });
    const dpiCheck = await checkPngDpiValue(result);
    console.log(`PNG DPI ayarlama sonrası kontrol: ${dpiCheck} DPI`);
    
    if (Math.abs(dpiCheck - dpi) > 1) {
      console.warn('DPI ayarlama tam olarak başarılı olmadı, alternatif yöntem deneniyor...');
      
      try {
        // Alternatif yöntem
        const manualResult = await manuallyFixPngDpi(blob, dpi);
        const manualDpiCheck = await checkPngDpiValue(manualResult);
        console.log(`Alternatif yöntem sonrası PNG DPI: ${manualDpiCheck}`);
        
        if (Math.abs(manualDpiCheck - dpi) <= 1) {
          return manualResult;
        }
      } catch (manualError) {
        console.warn('Alternatif yöntem başarısız:', manualError);
      }
    }
    
    return result;
  } catch (error) {
    console.error('PNG DPI metadata ekleme hatası:', error);
    // Hata durumunda orijinal görüntüyü döndür
    return blob;
  }
}

// Geliştirilmiş PNG DPI ayarlama
async function enhancedPngDpiAdjust(pngData: Uint8Array, dpi: number): Promise<Uint8Array> {
  try {
    // DPI'dan piksel/metre dönüşümü
    const INCHES_PER_METER = 39.3700787;
    const pixelsPerMeter = Math.round(dpi * INCHES_PER_METER);
    
    console.log(`PNG DPI dönüşümü: ${dpi} DPI = ${pixelsPerMeter} piksel/metre`);
    
    // PNG başlığını kontrol et
    if (pngData.length < 8 || 
        pngData[0] !== 0x89 || 
        pngData[1] !== 0x50 || // 'P'
        pngData[2] !== 0x4E || // 'N'
        pngData[3] !== 0x47) {
      throw new Error('Geçersiz PNG formatı');
    }
    
    // pHYs chunk'ı için yeni bir buffer oluştur
    const chunkLength = 9; // pHYs chunk veri uzunluğu
    const newChunk = new Uint8Array(12 + chunkLength);
    
    // Chunk uzunluğu (4 bytes)
    newChunk[0] = (chunkLength >> 24) & 0xff;
    newChunk[1] = (chunkLength >> 16) & 0xff;
    newChunk[2] = (chunkLength >> 8) & 0xff;
    newChunk[3] = chunkLength & 0xff;
    
    // "pHYs" chunk tipi (4 bytes)
    newChunk[4] = 0x70; // 'p'
    newChunk[5] = 0x48; // 'H'
    newChunk[6] = 0x59; // 'Y'
    newChunk[7] = 0x73; // 's'
    
    // X ekseni piksel/metre (4 bytes)
    newChunk[8] = (pixelsPerMeter >> 24) & 0xff;
    newChunk[9] = (pixelsPerMeter >> 16) & 0xff;
    newChunk[10] = (pixelsPerMeter >> 8) & 0xff;
    newChunk[11] = pixelsPerMeter & 0xff;
    
    // Y ekseni piksel/metre (4 bytes)
    newChunk[12] = (pixelsPerMeter >> 24) & 0xff;
    newChunk[13] = (pixelsPerMeter >> 16) & 0xff;
    newChunk[14] = (pixelsPerMeter >> 8) & 0xff;
    newChunk[15] = pixelsPerMeter & 0xff;
    
    // Birim tipi (1 byte) - 1 = metre
    newChunk[16] = 1;
    
    // CRC hesapla
    const crc = calculatePngCRC(newChunk.subarray(4, 17));
    newChunk[17] = (crc >> 24) & 0xff;
    newChunk[18] = (crc >> 16) & 0xff;
    newChunk[19] = (crc >> 8) & 0xff;
    newChunk[20] = crc & 0xff;
    
    // Yeni PNG oluştur
    const result = new Uint8Array(pngData.length + newChunk.length);
    
    // PNG başlığını kopyala
    result.set(pngData.subarray(0, 8), 0);
    
    // IHDR chunk'ını kopyala
    const ihdrLength = 13; // IHDR chunk veri uzunluğu
    result.set(pngData.subarray(8, 8 + 12 + ihdrLength), 8);
    
    // Yeni pHYs chunk'ı ekle
    result.set(newChunk, 8 + 12 + ihdrLength);
    
    // Kalan veriyi kopyala
    result.set(pngData.subarray(8 + 12 + ihdrLength), 8 + 12 + ihdrLength + newChunk.length);
    
    return result;
  } catch (error) {
    console.error('PNG DPI ayarlama hatası:', error);
    return pngData;
  }
}

// PNG için özel CRC hesaplama fonksiyonu
function calculatePngCRC(data: Uint8Array): number {
  let crc = 0xffffffff;
  
  for (let i = 0; i < data.length; i++) {
    let b = data[i];
    crc ^= b;
    
    for (let j = 0; j < 8; j++) {
      crc = (crc >>> 1) ^ ((crc & 1) ? 0xedb88320 : 0);
    }
  }
  
  return ~crc >>> 0;
}

// PNG'nin DPI değerini kontrol etme fonksiyonu - yeniden yazıldı
export async function checkPngDpiValue(blob: Blob): Promise<number> {
  try {
    const buffer = await blob.arrayBuffer();
    const data = new Uint8Array(buffer);
    
    // PNG formatını doğrula
    if (data.length < 8 || 
        data[0] !== 0x89 || 
        data[1] !== 0x50 || // 'P'
        data[2] !== 0x4E || // 'N' 
        data[3] !== 0x47) { // 'G'
      console.warn('Geçersiz PNG formatı (DPI kontrolü)');
      return 0;
    }
    
    // PNG chunk'larını tara
    let offset = 8; // PNG imzasından sonra başla
    
    while (offset < data.length - 12) {
      // Chunk uzunluğunu ve tipini oku
      const chunkLength = (data[offset] << 24) | (data[offset+1] << 16) | (data[offset+2] << 8) | data[offset+3];
      const chunkType = String.fromCharCode(data[offset+4], data[offset+5], data[offset+6], data[offset+7]);
      
      // pHYs chunk'ını bul
      if (chunkType === 'pHYs') {
        // Piksel/metre (X) değerini oku
        const pixelsPerMeter = (data[offset+8] << 24) | (data[offset+9] << 16) | (data[offset+10] << 8) | data[offset+11];
        
        // Birim değerini kontrol et (1 = metre)
        const unit = data[offset+16];
        
        if (unit === 1) {
          // DPI = piksel/metre ÷ 39.3700787
          const INCHES_PER_METER = 39.3700787;
          const dpi = Math.round(pixelsPerMeter / INCHES_PER_METER);
          
          console.log(`PNG DPI değeri: ${dpi} DPI (${pixelsPerMeter} piksel/metre)`);
          return dpi;
        } else {
          console.log(`PNG pHYs chunk bulundu fakat birim metre değil (${unit})`);
        }
      }
      
      // Sonraki chunk'a git
      offset += 4 + 4 + chunkLength + 4; // 4 (uzunluk) + 4 (tip) + veri + 4 (CRC)
    }
    
    console.log('PNG dosyasında DPI bilgisi (pHYs chunk) bulunamadı');
    return 0;
  } catch (error) {
    console.error('PNG DPI kontrol hatası:', error);
    return 0;
  }
}

// API temelli PNG DPI çözümü
export const fixPngDpi = async (file: File, dpi: number): Promise<Blob> => {
  try {
    console.log(`PNG DPI düzeltme API isteği gönderiliyor: ${API_URL}/fix-png-dpi - DPI: ${dpi}`);
    
    const formData = new FormData();
    formData.append('image', file);
    formData.append('dpi', dpi.toString());
    
    // Özel DPI düzeltme endpoint'ini kullan
    const response = await fetch(`${API_URL}/fix-png-dpi`, {
      method: 'POST',
      body: formData
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`PNG DPI düzeltme API hatası: ${response.status} - ${errorText}`);
      throw new Error(`PNG DPI düzeltme API hatası: ${response.status} - ${errorText}`);
    }
    
    console.log('PNG DPI API ile başarıyla düzeltildi');
    const result = await response.blob();
    return result;
  } catch (error) {
    console.error('API PNG DPI düzeltme hatası:', error);
    throw error;
  }
}

// Manuel PNG DPI ayarlama fonksiyonu - daha doğru hesaplama ile güncellendi
async function legacyPngDpiAdjust(pngData: Uint8Array, dpi: number): Promise<Uint8Array> {
  // PNG dosya yapısı parçalarını hazırla
  const metersPerInch = 0.0254;
  // Math.round yerine Math.ceil kullanarak yukarı yuvarlama yapalım
  const pixelsPerMeter = Math.ceil(dpi / metersPerInch);
  
  try {
    // PNG imzası ve başlık kontrolü
    if (pngData.length < 33 || 
        pngData[0] !== 0x89 || // PNG imzası kontrolü
        String.fromCharCode(pngData[1], pngData[2], pngData[3]) !== 'PNG') {
      console.warn('Geçerli bir PNG imzası bulunamadı');
      return pngData;
    }
    
    // IHDR chunk'ı bul ve uzunluğunu oku
    const ihdrLength = (pngData[8] << 24) | (pngData[9] << 16) | (pngData[10] << 8) | pngData[11];
    
    // IHDR chunk'ının doğru tipte olduğunu kontrol et
    if (String.fromCharCode(pngData[12], pngData[13], pngData[14], pngData[15]) !== 'IHDR') {
      console.warn('PNG IHDR chunk beklenilen konumda bulunamadı');
      return pngData;
    }
    
    // IHDR chunk'ının bittiği pozisyonu hesapla: 8 (signature) + 4 (length) + 4 (type) + length + 4 (CRC)
    const ihdrEnd = 8 + 4 + 4 + ihdrLength + 4;
    
    // pHYs chunk hazırla
    const physChunk = new Uint8Array(21); // 4 (length) + 4 (type) + 9 (data) + 4 (CRC)
    
    // Chunk uzunluğu (9 byte)
    physChunk[0] = 0;
    physChunk[1] = 0;
    physChunk[2] = 0;
    physChunk[3] = 9;
    
    // Chunk tipi "pHYs"
    physChunk[4] = 0x70; // 'p'
    physChunk[5] = 0x48; // 'H'
    physChunk[6] = 0x59; // 'Y'
    physChunk[7] = 0x73; // 's'
    
    // X ve Y ekseni piksel/metre (4 byte her biri, tam 32-bit değer)
    physChunk[8] = (pixelsPerMeter >>> 24) & 0xFF;
    physChunk[9] = (pixelsPerMeter >>> 16) & 0xFF;
    physChunk[10] = (pixelsPerMeter >>> 8) & 0xFF;
    physChunk[11] = pixelsPerMeter & 0xFF;
    
    physChunk[12] = (pixelsPerMeter >>> 24) & 0xFF;
    physChunk[13] = (pixelsPerMeter >>> 16) & 0xFF;
    physChunk[14] = (pixelsPerMeter >>> 8) & 0xFF;
    physChunk[15] = pixelsPerMeter & 0xFF;
    
    // Ölçü birimi (1 = metre)
    physChunk[16] = 1;
    
    // CRC hesapla
    const typeAndData = new Uint8Array(13); // 4 (type) + 9 (data)
    typeAndData.set(physChunk.slice(4, 17));
    
    let crc = 0xFFFFFFFF;
    for (let i = 0; i < typeAndData.length; i++) {
      crc = updateCRC(crc, typeAndData[i]);
    }
    crc = crc ^ 0xFFFFFFFF;
    
    // CRC değerini yaz
    physChunk[17] = (crc >>> 24) & 0xFF;
    physChunk[18] = (crc >>> 16) & 0xFF;
    physChunk[19] = (crc >>> 8) & 0xFF;
    physChunk[20] = crc & 0xFF;
    
    // Yeni PNG oluştur
    const result = new Uint8Array(pngData.length + physChunk.length);
    
    // PNG imzası ve IHDR
    result.set(pngData.slice(0, ihdrEnd));
    
    // pHYs chunk ekle
    result.set(physChunk, ihdrEnd);
    
    // Kalan chunk'ları ekle
    result.set(pngData.slice(ihdrEnd), ihdrEnd + physChunk.length);
    
    console.log(`PNG DPI manuel olarak ayarlandı: ${dpi} DPI (${pixelsPerMeter} piksel/metre)`);
    return result;
  } catch (error) {
    console.error('Manuel PNG DPI ayarlama hatası:', error);
    return pngData; // Hata durumunda orijinal veriyi döndür
  }
}

// PNG CRC hesaplamak için yardımcı fonksiyon
function updateCRC(crc: number, byte: number): number {
  const CRC_TABLE = generateCRCTable();
  return ((crc >>> 8) ^ CRC_TABLE[(crc ^ byte) & 0xFF]) >>> 0;
}

// CRC tablosu oluştur
function generateCRCTable(): Uint32Array {
  const table = new Uint32Array(256);
  
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) {
      c = c & 1 ? 0xEDB88320 ^ (c >>> 1) : c >>> 1;
    }
    table[n] = c >>> 0;
  }
  
  return table;
}

// JPEG metadata ekleme fonksiyonu
function addJpegMetadata(data: Uint8Array, dpi: number): Uint8Array {
  // JFIF segmenti için DPI bilgisi
  const jfifData = new Uint8Array([
    0xFF, 0xE0, // JFIF marker
    0x00, 0x10, // length
    0x4A, 0x46, 0x49, 0x46, 0x00, // JFIF identifier
    0x01, 0x01, // version
    0x01, // units (1 = dots per inch)
    (dpi >> 8) & 0xFF, dpi & 0xFF, // horizontal resolution
    (dpi >> 8) & 0xFF, dpi & 0xFF, // vertical resolution
    0x00, 0x00 // thumbnail
  ]);

  // Yeni buffer oluştur
  const output = new Uint8Array(data.length + jfifData.length);
  output.set([0xFF, 0xD8]); // JPEG start marker
  output.set(jfifData, 2);
  output.set(data.subarray(2), jfifData.length);

  return output;
}

// PNG metadata ekleme fonksiyonu - bu fonksiyonu düzeltelim
function addPngMetadata(data: Uint8Array, dpi: number): Uint8Array {
  try {
    // PNG pHYs chunk için metre cinsinden DPI hesapla
    const metersPerInch = 0.0254;
    const pixelsPerMeter = Math.round(dpi / metersPerInch);
    
    // PNG header ve IHDR chunk'ı koruyalım
    // PNG signature: 8 bytes
    // IHDR chunk: 25 bytes (4 byte length, 4 byte type, 13 byte data, 4 byte CRC)
    const pngSignature = data.subarray(0, 8);
    let ihdrChunk = null;
    
    // IHDR chunk'ı bul
    for (let i = 8; i < data.length - 12; i++) {
      if (
        data[i + 4] === 0x49 && // 'I'
        data[i + 5] === 0x48 && // 'H'
        data[i + 6] === 0x44 && // 'D'
        data[i + 7] === 0x52    // 'R'
      ) {
        const chunkLength = (data[i] << 24) | (data[i + 1] << 16) | (data[i + 2] << 8) | data[i + 3];
        ihdrChunk = data.subarray(i, i + 12 + chunkLength); // 12 = 4 (length) + 4 (type) + data + 4 (CRC)
        break;
      }
    }
    
    if (!ihdrChunk) {
      console.warn('PNG IHDR chunk not found');
      return data; // Orijinal veriyi döndür
    }
    
    // pHYs chunk oluştur
    const pHYsChunkData = new Uint8Array([
      0, 0, 0, 9, // Length (9 bytes of data)
      112, 72, 89, 115, // "pHYs"
      (pixelsPerMeter >> 24) & 0xFF,
      (pixelsPerMeter >> 16) & 0xFF,
      (pixelsPerMeter >> 8) & 0xFF,
      pixelsPerMeter & 0xFF,
      (pixelsPerMeter >> 24) & 0xFF,
      (pixelsPerMeter >> 16) & 0xFF,
      (pixelsPerMeter >> 8) & 0xFF,
      pixelsPerMeter & 0xFF,
      1, // Unit is meter
      0, 0, 0, 0 // CRC placeholder
    ]);
    
    // CRC hesapla (gerçek CRC hesaplaması zor olduğu için basit bir placholder)
    // Pratikte çoğu PNG görüntüleyici CRC'yi kontrol etmeyecek
    
    // Orijinal PNG'nin geri kalanı (IHDR'dan sonraki tüm chunklar)
    const remainingData = data.subarray(8 + ihdrChunk.length);
    
    // Yeni PNG oluştur: signature + IHDR + pHYs + remaining data
    const output = new Uint8Array(pngSignature.length + ihdrChunk.length + pHYsChunkData.length + remainingData.length);
    let offset = 0;
    
    output.set(pngSignature, offset);
    offset += pngSignature.length;
    
    output.set(ihdrChunk, offset);
    offset += ihdrChunk.length;
    
    output.set(pHYsChunkData, offset);
    offset += pHYsChunkData.length;
    
    output.set(remainingData, offset);
    
    return output;
  } catch (error) {
    console.error('PNG metadata adding failed:', error);
    return data; // Hata durumunda orijinal veriyi döndür
  }
}

// Daha basit bir PNG metadata çözümü (alternatif)
function addSimplePngMetadata(data: Uint8Array, dpi: number): Uint8Array {
  // PNG dosyalarını değiştirirken çok dikkatli olmak gerekiyor
  // Bu basit çözüm metadata eklemeden dosyayı olduğu gibi döndürür
  return data;
}

// downloadProcessedImage fonksiyonunu güncelle - kapsamlı doğrulama
export const downloadProcessedImage = async (
  file: File,
  targetSize: PrintSize,
  outputFormat: OutputFormat,
  fileName: string,
  dpi: number = 300,
  // useApi parametresi artık PDF için anlamsız, kaldırılabilir veya sadece PNG/JPEG için kullanılabilir
): Promise<void> => {
  try {
    console.log(`İndirme/Dönüştürme işlemi başladı - Format: ${outputFormat}, Boyut: ${targetSize.width}x${targetSize.height}, DPI: ${dpi}`);
    
    if (outputFormat === 'pdf') {
      console.log('PDF dönüştürme talep edildi - Sunucu tarafı kullanılacak');
      
      const formData = new FormData();
      formData.append('image', file);
      formData.append('targetWidth', targetSize.width.toString());
      formData.append('targetHeight', targetSize.height.toString());
      formData.append('dpi', dpi.toString());
      formData.append('fileName', fileName); // Dosya adını da gönderelim

      toast.loading('PDF oluşturuluyor (Sunucu)... ', { id: 'pdf-server-conversion' });

      try {
        const response = await fetch(`${API_URL}/convert-to-pdf`, {
          method: 'POST',
          body: formData,
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error(`Sunucu PDF dönüştürme hatası: ${response.status} - ${errorText}`);
          throw new Error(`Sunucu hatası (${response.status}): PDF oluşturulamadı. Detay: ${errorText}`);
        }

        // Yanıt olarak gelen PDF blob'unu al
        const pdfBlob = await response.blob();
        
        if (pdfBlob.size === 0) {
            throw new Error('Sunucudan boş PDF verisi alındı.');
        }

        // PDF'i indir
        const downloadUrl = URL.createObjectURL(pdfBlob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        // Sunucu Content-Disposition ayarlamazsa diye dosya adını burada belirle
        a.download = `${fileName}.pdf`; 
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(downloadUrl);

        toast.success('PDF başarıyla oluşturuldu ve indirildi (Sunucu).', { id: 'pdf-server-conversion' });
        console.log('Sunucu tarafı PDF dönüştürme ve indirme başarılı.');

      } catch (error) {
        toast.error('PDF oluşturulurken sunucu hatası oluştu.', { id: 'pdf-server-conversion' });
        console.error('Sunucu PDF dönüştürme fetch hatası:', error);
        // Hata durumunda kullanıcıya bilgi ver
        alert(`PDF oluşturulamadı (Sunucu): ${error instanceof Error ? error.message : 'Bilinmeyen ağ hatası'}`);
        throw error; // Hatanın yukarıya iletilmesi
      }

    } else {
      // --- PNG/JPEG işleme (Mevcut kod korunur) --- 
      console.log(`Görüntü işleme (${outputFormat}) başlıyor...`);
      let blob;
      // API kullanımı burada hala geçerli olabilir (optimizeImageViaAPI)
      // Şimdilik basitlik adına sadece client-side varsayalım
      // let fromClientSide = !useApi; 
      // if (useApi) { ... } else { ... }
      
      console.log('Client tarafında görüntü işleniyor...');
      blob = await processImage(file, targetSize, outputFormat, dpi);
      console.log('Client-side görüntü işleme başarılı');
      
      // DPI metadata ekle (Bu kısım hala gerekli)
      if (outputFormat === 'png') {
        blob = await addMetadataToPng(blob, dpi);
      } else if (outputFormat === 'jpeg') {
        blob = await addMetadataToJpeg(blob, dpi);
      }
      
      const finalFileName = `${fileName.replace(/\.[^/.]+$/, "")}_${dpi}dpi.${outputFormat}`;
      
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = finalFileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      console.log(`Dosya başarıyla indirildi: ${finalFileName} (client-side işleme)`);
      // --- PNG/JPEG işleme sonu --- 
    }
  } catch (error) {
    // Genel hataları yakala (örn: processImage hatası)
    console.error('Download/Process Genel Hata:', error);
    toast.error('İşlem sırasında bir hata oluştu.');
    // throw error; // Tekrar fırlatmaya gerek olmayabilir, zaten loglandı/alert verildi
  }
};

// DPI hesaplama fonksiyonunu güncelliyoruz
const calculateImageDpi = async (file: File): Promise<number> => {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const tags = await ExifReader.load(arrayBuffer);

    // XResolution ve YResolution değerlerini güvenli bir şekilde al
    const xRes = tags.XResolution?.value;
    const yRes = tags.YResolution?.value;
    
    if (xRes || yRes) {
      // ExifTag değerlerini sayıya dönüştür
      const getResolutionValue = (value: any): number => {
        if (Array.isArray(value)) {
          // Kesirli değer olarak geliyorsa (örn: [300, 1])
          return value[0] / (value[1] || 1);
        }
        return typeof value === 'number' ? value : 72;
      };

      const xDpi = xRes ? getResolutionValue(xRes) : 0;
      const yDpi = yRes ? getResolutionValue(yRes) : 0;
      
      // En yüksek çözünürlük değerini al
      const dpi = Math.max(xDpi, yDpi);
      return dpi > 0 ? Math.round(dpi) : 72;
    }

    // EXIF yoksa veya DPI bilgisi bulunamadıysa alternatif yöntem
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        if ('naturalWidth' in img) {
          // DPI tahmini yap
          const screenPPI = 96; // Standart ekran PPI değeri
          const naturalWidth = img.naturalWidth;
          const displayWidth = img.width;
          
          // Görüntü boyutuna göre yaklaşık DPI hesapla
          const estimatedDpi = Math.round((naturalWidth / displayWidth) * screenPPI);
          resolve(estimatedDpi > 72 ? estimatedDpi : 72);
        } else {
          resolve(72); // Varsayılan değer
        }
        URL.revokeObjectURL(img.src);
      };
      img.onerror = () => {
        URL.revokeObjectURL(img.src);
        resolve(72);
      };
      img.src = URL.createObjectURL(file);
    });
  } catch (error) {
    console.warn('DPI extraction error:', error);
    return 72; // Hata durumunda varsayılan değer
  }
};

export async function getFileInfo(file: File): Promise<FileInfo> {
  return new Promise(async (resolve, reject) => {
    try {
      let fileInfo: FileInfo = {
        dimensions: { width: 0, height: 0 },
        fileType: file.type,
        dpi: 72,
        size: file.size
      };

      if (file.type === 'application/pdf') {
        const pdfInfo = await processPdfInfo(file, fileInfo);
        resolve(pdfInfo);
        return;
      }

      const img = new Image();
      img.onload = async () => {
        fileInfo.dimensions = {
          width: img.width,
          height: img.height
        };

        // DPI hesapla
        fileInfo.dpi = await calculateImageDpi(file);
        
        URL.revokeObjectURL(img.src);
        resolve(fileInfo);
      };
      
      img.onerror = () => {
        URL.revokeObjectURL(img.src);
        reject(new Error('Failed to load image'));
      };
      
      img.src = URL.createObjectURL(file);
    } catch (error) {
      reject(error);
    }
  });
};

// PDF'den görüntüye dönüştürme fonksiyonunu düzeltelim
export async function convertPdfToImage(file: File, format: string, dpi: number): Promise<File> {
  console.log('PDF dönüştürme başlıyor...');
  
  try {
    // Worker URL'sini öncelikle kontrol et ve ayarla
    const pdfjsLib = await import('pdfjs-dist');
    
    // Worker URL'sinin doğru şekilde ayarlanması kritik
    if (!pdfjsLib.GlobalWorkerOptions.workerSrc) {
      // CDN worker kaynağını kullan - sürüm ve yol doğru olmalı
      const workerUrl = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
      pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;
      console.log('PDF.js worker ayarlandı:', pdfjsLib.GlobalWorkerOptions.workerSrc);
    }

    // PDF yükleme işlemi
    const arrayBuffer = await file.arrayBuffer();
    const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
    const pdfDocument = await loadingTask.promise;
    
    console.log('PDF yüklendi, toplam sayfa sayısı:', pdfDocument.numPages);
    
    // İlk sayfayı al
    const page = await pdfDocument.getPage(1);

    // Doğru DPI için ölçek hesapla (standart PDF 72 DPI'dır)
    const scale = dpi / 72;
    const viewport = page.getViewport({ scale });
    
    console.log(`Viewport boyutları: ${viewport.width} x ${viewport.height}, scale: ${scale}`);
    
    // Canvas oluştur
    const canvas = document.createElement('canvas');
    canvas.width = Math.floor(viewport.width);
    canvas.height = Math.floor(viewport.height);
    
    // Canvas bağlamını al
    const context = canvas.getContext('2d', { alpha: false });
    
    if (!context) {
      throw new Error('Canvas context oluşturulamadı');
    }
    
    // Arka planı beyaz yap - PDF şeffaf olabilir
    context.fillStyle = 'white';
    context.fillRect(0, 0, canvas.width, canvas.height);
    
    // Render işlemi için ayarlar
    const renderContext = {
      canvasContext: context,
      viewport,
      // Render kalitesini artır
      renderInteractiveForms: true,
      enableWebGL: true
    };
    
    // Render işlemini başlat ve tamamlanmasını bekle
    console.log('PDF rendering başlıyor...');
    const renderTask = page.render(renderContext);
    await renderTask.promise;
    console.log('PDF rendering tamamlandı');
    
    // Canvas içeriğini görüntü formatına dönüştür
    let outputBlob: Blob;
    
    if (format === 'jpeg') {
      outputBlob = await new Promise<Blob>((resolve) => {
        // JPEG oluştur - yüksek kalite (0.95)
        canvas.toBlob((blob) => {
          resolve(blob || new Blob([]));
        }, 'image/jpeg', 0.95);
      });
    } else {
      outputBlob = await new Promise<Blob>((resolve) => {
        // PNG oluştur - varsayılan kalite
        canvas.toBlob((blob) => {
          resolve(blob || new Blob([]));
        }, 'image/png');
      });
    }
    
    console.log(`Canvas görüntüsü oluşturuldu, boyut: ${outputBlob.size} bytes`);
    
    // Dönüştürme kalitesini kontrol et
    if (outputBlob.size < 1024) {
      console.warn('Oluşturulan görüntü çok küçük, içerik olmayabilir');
    }
    
    // DPI meta verisi ekle (gerekirse)
    let finalBlob = outputBlob;
    if (format === 'png') {
      finalBlob = await addMetadataToPng(outputBlob, dpi);
    } else if (format === 'jpeg') {
      finalBlob = await addMetadataToJpeg(outputBlob, dpi);
    }
    
    // File nesnesine dönüştür ve döndür
    const outputFile = new File(
      [finalBlob],
      `${file.name.replace(/\.[^/.]+$/, "")}.${format}`,
      { type: format === 'jpeg' ? 'image/jpeg' : 'image/png' }
    );
    
    console.log(`PDF dönüştürme tamamlandı, dosya boyutu: ${outputFile.size} bytes`);
    return outputFile;
    
  } catch (error) {
    console.error('PDF dönüştürme hatası:', error);
    throw new Error(`PDF dönüştürme işlemi başarısız: ${error instanceof Error ? error.message : 'Bilinmeyen hata'}`);
  }
}

// PDF'ten görüntü bilgilerini alma fonksiyonunu güncelle
export const getPdfInfo = async (file: File): Promise<FileInfo> => {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    const page = await pdf.getPage(1);
    const viewport = page.getViewport({ scale: 1 });

    return {
      dimensions: {
        width: Math.round(viewport.width),
        height: Math.round(viewport.height)
      },
      dpi: 72, // PDF'ler standart olarak 72 DPI
      fileType: 'application/pdf',
      size: file.size
    };
  } catch (error) {
    console.error('PDF info extraction error:', error);
    throw error;
  }
};

// Function to estimate output file size
export function estimateOutputSize(
  inputSize: number,
  originalDimensions: { width: number; height: number },
  targetSize: PrintSize,
  outputFormat: OutputFormat,
  originalDpi: number = 72,
  targetDpi: number = 300
): number {
  // Calculate size ratio based on dimensions
  const originalPixels = originalDimensions.width * originalDimensions.height;
  const targetPixels = targetSize.width * targetSize.height;
  const pixelRatio = targetPixels / originalPixels;
  
  // Calculate size ratio based on DPI
  const dpiRatio = (targetDpi * targetDpi) / (originalDpi * originalDpi);
  
  // Base estimate - scale by area and DPI
  let estimatedSize = inputSize * pixelRatio * dpiRatio;
  
  // Format-specific adjustments
  switch (outputFormat) {
    case 'jpeg':
      // JPEG uses compression, so typically smaller
      estimatedSize *= 0.7;
      break;
    case 'png':
      // PNG is lossless and can be larger
      estimatedSize *= 1.2;
      break;
    case 'pdf':
      // PDF with embedded image
      estimatedSize *= 0.9;
      break;
  }
  
  return Math.round(estimatedSize);
}
async function processPdfInfo(file: File, fileInfo: FileInfo): Promise<FileInfo> {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    const page = await pdf.getPage(1);
    const viewport = page.getViewport({ scale: 1 });

    fileInfo.dimensions = {
      width: Math.round(viewport.width),
      height: Math.round(viewport.height)
    };

    return fileInfo;
  } catch (error) {
    throw new Error('Failed to process PDF info');
  }
}

// API aracılığıyla görüntü işleme için yeni fonksiyon
export const optimizeImageViaAPI = async (
  file: File,
  targetSize: PrintSize,
  outputFormat: OutputFormat,
  dpi: number = 300
): Promise<Blob> => {
  const formData = new FormData();
  formData.append('image', file);
  formData.append('size', JSON.stringify(targetSize));
  formData.append('format', outputFormat);
  formData.append('dpi', dpi.toString());

  console.log(`Görüntü optimize API isteği: ${API_URL}/optimize - Format: ${outputFormat}, Boyut: ${targetSize.width}x${targetSize.height}, DPI: ${dpi}`);

  try {
    const response = await fetch(`${API_URL}/optimize`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Görüntü optimize API hatası: ${response.status} - ${errorText}`);
      throw new Error(`API Error: ${response.status} - ${errorText}`);
    }

    console.log('Görüntü optimize API isteği başarılı');
    return await response.blob();
  } catch (error) {
    console.error('API optimize error:', error);
    throw error;
  }
};

// PNG DPI bilgisini okuma fonksiyonu (test için)
export async function readPngDpi(file: File): Promise<number> {
  try {
    const buffer = await file.arrayBuffer();
    const data = new Uint8Array(buffer);
    
    // PNG formatı doğrula
    if (data.length < 33 || 
        data[0] !== 0x89 || 
        String.fromCharCode(data[1], data[2], data[3]) !== 'PNG') {
      throw new Error('Geçerli bir PNG dosyası değil');
    }
    
    let index = 8; // PNG imzasından sonraki ilk chunk başlangıcı
    
    // Tüm chunk'ları tara, pHYs chunk'ını bul
    while (index < data.length) {
      const chunkLength = (data[index] << 24) | (data[index + 1] << 16) | (data[index + 2] << 8) | data[index + 3];
      const chunkType = String.fromCharCode(data[index + 4], data[index + 5], data[index + 6], data[index + 7]);
      
      if (chunkType === 'pHYs') {
        // pHYs chunk bulundu, DPI değerini hesapla
        const xPixelsPerMeter = (data[index + 8] << 24) | (data[index + 9] << 16) | (data[index + 10] << 8) | data[index + 11];
        const unit = data[index + 16]; // 1 = metre
        
        if (unit === 1) {
          // Metre cinsinden değeri DPI'a çevir
          const metersPerInch = 0.0254;
          const dpi = Math.round(xPixelsPerMeter * metersPerInch);
          console.log(`PNG dosyasından DPI değeri okundu: ${dpi} DPI (${xPixelsPerMeter} piksel/metre)`);
          return dpi;
        }
      }
      
      // Sonraki chunk'a geç: 4 (length) + 4 (type) + length + 4 (CRC)
      index += 4 + 4 + chunkLength + 4;
    }
    
    console.log('PNG dosyasında pHYs chunk bulunamadı, varsayılan DPI: 72');
    return 72; // pHYs chunk bulunamazsa varsayılan değer
  } catch (error) {
    console.error('PNG DPI okuma hatası:', error);
    return 72; // Hata durumunda varsayılan değer
  }
}

// PNG DPI ekleme fonksiyonunu düzelt - alternatif implementasyon
export async function addDpiMetadataToPng(imageData: Blob, dpi: number): Promise<Blob> {
  try {
    console.log('PNG DPI ayarlama başladı - Hedef DPI:', dpi);
    
    // Manuel yöntemle devam et
    console.log('Manuel yöntemle devam ediliyor...');
    const result = await manuallyFixPngDpi(imageData, dpi);
    console.log('Manuel düzeltme sonrası PNG DPI:', dpi);
    return result;
  } catch (error) {
    console.error('PNG metadata ekleme hatası:', error);
    // Hata durumunda orijinal görüntüyü geri döndür
    return imageData;
  }
}

// PNG metadata düzeltme işlemi
// PNG DPI manuel düzeltme işlemini gerçekleştir
async function manuallyFixPngDpi(imageData: Blob, dpi: number): Promise<Blob> {
  console.log(`PNG DPI ayarlanıyor: ${dpi} DPI`);
  
  try {
    // PNG dosyasını array buffer olarak oku
    const arrayBuffer = await imageData.arrayBuffer();
    const buffer = new Uint8Array(arrayBuffer);
    
    // enhancedPngDpiAdjust fonksiyonunu çağırarak işlemi gerçekleştir
    const result = await enhancedPngDpiAdjust(buffer, dpi);
    
    // Sonucu Blob olarak döndür
    return new Blob([result], { type: 'image/png' });
  } catch (error) {
    console.error('Manuel PNG DPI düzeltme hatası:', error);
    return imageData; // Hata durumunda orijinal görüntüyü döndür
  }
}

// PDF'den görüntü çıkarma fonksiyonu - Alternatif yaklaşım
export async function extractImageFromPdf(file: File): Promise<Blob> {
  console.log('PDF görüntü çıkarma başladı');
  
  try {
    // PDF.js modülünü dinamik olarak import et
    const pdfjsLib = await import('pdfjs-dist');
    
    // Worker yolu ayarla
    if (!pdfjsLib.GlobalWorkerOptions.workerSrc) {
      pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.10.38/pdf.worker.min.js';
    }
    
    // PDF dosyasını oku
    const data = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data }).promise;
    
    // İlk sayfayı al
    const page = await pdf.getPage(1);
    
    // A4 boyutu için yaklaşık 300 DPI için scale hesapla
    const scale = 300 / 72;  // 300 DPI / 72 (PDF'in dahili DPI)
    const viewport = page.getViewport({ scale });
    
    // Canvas hazırla
    const canvas = document.createElement('canvas');
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    
    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('Canvas 2D context oluşturulamadı');
    }
    
    // Sayfa render
    await page.render({
      canvasContext: context,
      viewport
    }).promise;
    
    // Canvas içeriğini PNG olarak al
    const imageBlob = await new Promise<Blob>((resolve) => {
      canvas.toBlob((blob) => {
        resolve(blob || new Blob([]));
      }, 'image/png');
    });
    
    console.log('PDF görüntü çıkarma tamamlandı');
    return imageBlob;
  } catch (error) {
    console.error('PDF görüntü çıkarma hatası:', error);
    throw error;
  }
}

export async function testPdf(): Promise<Blob> {
  try {
    console.log('Testing simple PDF generation');
    const response = await fetch('/api/test-pdf-alt', {
      method: 'GET',
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`Server error: ${errorData.error || response.statusText}`);
    }

    // Directly get the PDF as a blob
    const pdfBlob = await response.blob();
    console.log(`Received test PDF blob: ${pdfBlob.size} bytes, type: ${pdfBlob.type}`);
    
    return pdfBlob;
  } catch (error) {
    console.error('Error testing PDF generation:', error);
    throw error;
  }
}


// Encode a canvas as a print file with the given DPI stored in it.
// JPEG/PNG get DPI metadata; PDF gets a page sized canvas/dpi inches.
export async function canvasToPrintFile(
  canvas: HTMLCanvasElement,
  format: 'jpeg' | 'png' | 'pdf',
  dpi: number
): Promise<Blob> {
  const mime = format === 'png' ? 'image/png' : 'image/jpeg';
  const raw: Blob = await new Promise((resolve, reject) =>
    canvas.toBlob(b => (b ? resolve(b) : reject(new Error('Could not export image'))), mime, mime === 'image/jpeg' ? 0.95 : undefined)
  );
  if (format === 'pdf') return jpegToPdf(raw, canvas.width / dpi, canvas.height / dpi);
  if (format === 'png') return addMetadataToPng(raw, dpi);
  return addMetadataToJpeg(raw, dpi);
}

// Load a File into an HTMLImageElement
export function loadImageFile(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('This file could not be opened as an image.'));
    img.src = URL.createObjectURL(file);
  });
}

// Draw an image so it covers the whole canvas (center crop), high-quality resampling
export function drawCover(ctx: CanvasRenderingContext2D, img: CanvasImageSource & { width: number; height: number }, w: number, h: number,
  src?: { x: number; y: number; w: number; h: number }) {
  let sx = 0, sy = 0, sw = img.width, sh = img.height;
  if (src) { sx = src.x; sy = src.y; sw = src.w; sh = src.h; }
  else {
    const target = w / h, source = sw / sh;
    if (source > target) { sw = Math.round(sh * target); sx = Math.round((img.width - sw) / 2); }
    else { sh = Math.round(sw / target); sy = Math.round((img.height - sh) / 2); }
  }
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, w, h);
}

// Browsers (especially Safari/iOS) fail on very large canvases
export const MAX_CANVAS_PIXELS = 16_000_000;
